<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useWindowSize } from '@vueuse/core';
import CircularProgressIndicator from '../progress/CircularProgressIndicator.vue';
import { disableScrollLock, enableScrollLock } from '../../../utils/scroll-lock';

const props = defineProps({
    limit: {
        type: Number,
    },
    remainingWarning: {
        type: Number,
        default: 20,
    },
    status: {
        type: String,
    },
    trim: {
        type: Boolean,
        default: true,
    },
    placeholder: {
        type: String,
    },
    dataQa: {
        type: String,
    },
});

const model = defineModel({
    type: String,
});

const emit = defineEmits(['blur']);

const stringValue = computed(() => model.value ?? '');

const htmlValue = computed(() => (props.limit ?
    `${stringValue.value.slice(0, props.limit)}<em>${stringValue.value.slice(props.limit)}</em>` :
    stringValue.value));

const textarea = ref(null);
const limitStatus = computed(() => (stringValue.value.length / props.limit) * 100);
const remainingCharacters = computed(() => props.limit - stringValue.value.length);
const initialRows = computed(() => textarea.value.rows);
const textareaStyle = computed(() => getComputedStyle(textarea.value));

const updateValue = (e) => {
    // this is related to the cursor position.
    // the observation of the position of cursor is lost,
    // only when it resets the template by clicking the button.
    // so the last cursor position should get manually given.
    const cursorPoint = e.target.selectionStart;
    const element = e.target;
    window.requestAnimationFrame(() => {
        element.selectionStart = cursorPoint;
        element.selectionEnd = cursorPoint;
    });

    model.value = e.target.value;
};

// Update the size of the textarea to fit the number of lines of text.
const textareaGrow = () => {
    // This is only a convenience function. So we catch errors and fail
    // silently to not break the app if an error happens.
    try {
        // In certain browsers (at least Firefox) we need to prevent scrolling to stop the
        // browser from scrolling randomly when calculating the new row number.
        enableScrollLock();

        const paddingTop = parseInt(textareaStyle.value.getPropertyValue('padding-top'), 10);
        const paddingBottom = parseInt(textareaStyle.value.getPropertyValue('padding-bottom'), 10);
        const lineHeight = parseInt(textareaStyle.value.getPropertyValue('line-height'), 10);

        // Resetting the row count is necessary for recalculating the `scrollHeight` of the textarea.
        textarea.value.rows = initialRows.value - 1;
        const innerHeight = textarea.value.scrollHeight - paddingTop - paddingBottom;
        // An additional row (+ 1) is added to make space for the limit indicator.
        textarea.value.rows = (innerHeight / lineHeight) + 1;

        disableScrollLock();
    } catch (error) {
        if (import.meta.env.MODE === 'development') {
            // eslint-disable-next-line no-console
            console.error(error);
        }
    }
};
const { width } = useWindowSize();
watch(width, textareaGrow);

const trimValueOnBlur = (event) => {
    model.value = props.trim ? event.target.value.trim() : event.target.value;
    emit('update:modelValue', model.value);
};

// Triggering `textareaGrow()` in both, the `mounted()` lifecycle hook and in the
// `watch.value()` method, is necessary, because watchers with `immediate: true`
// are triggered before evaluating computed properties.
watch(model, async () => {
    await nextTick();
    textareaGrow();
});

onMounted(textareaGrow);

function focusTextarea() {
    textarea.value.focus();
}

defineExpose({ focusTextarea });

</script>

<script>
export default {
    inheritAttrs: false,
};
</script>

<template>
    <div class="c-limitedTextarea">
        <div
            class="c-limitedTextarea__htmlarea"
            aria-hidden="true"
            data-qa="ghost content"
            v-html="htmlValue"
        />
        <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
        <textarea
            ref="textarea"
            :class="{
                [`has-status-${status}`]: status,
            }"
            :value="model"
            v-bind="$attrs"
            class="c-limitedTextarea__textarea"
            :placeholder="placeholder"
            :data-qa
            @input="updateValue"
            @blur="emit('blur', $event); trimValueOnBlur($event)"
        />
        <div class="c-limitedTextarea__outer">
            <div class="c-limitedTextarea__inner">
                <div
                    v-if="limit"
                    class="k-c-circularProgressIndicator k-c-circularProgressIndicator--bottomRightPosition
                    c-limitedTextarea__limit"
                    data-qa="character counter"
                >
                    <span
                        class="k-c-circularProgressIndicator__text"
                        :class="{
                            'k-c-circularProgressIndicator__text--red': remainingCharacters < 0,
                        }"
                    >
                        {{ remainingCharacters }}
                    </span>
                    <CircularProgressIndicator
                        :progress="limitStatus"
                        :warning="remainingCharacters <= remainingWarning"
                        :error="remainingCharacters < 0"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
@use '~@karriereat/global-styles/scss/tools/typo';
@use '../../../assets/scss/components/input';
@use '~@karriereat/global-styles/scss/settings/spacing';

$custom-bottom-spacing: 0.6rem;
$endIcon-bottom-visual-correction: 2px;
$counter-text-gap: 40px;
$textarea-height: 100px;

.c-limitedTextarea {
    position: relative;

    // 1. Make sure font styling is exactly the same.
    &__htmlarea,
    &__textarea {
        @include input.input();
        @include typo.k-typo-m(); // 1
    }

    // 1. Remove the element from the normal document flow, so the <textarea> lies above
    //    this element, and make the HTML area <div> as tall as the <textarea>.
    // 2. Make word breaks behave exactly like in a textarea.
    &__htmlarea {
        position: absolute; // 1
        height: 100%; // 1
        background: var(--k-white);
        color: transparent;
        white-space: pre-wrap; // 2
        word-wrap: break-word; // 2
    }

    // 1. Make the <textarea> a block level element to make its sizing behave like that of a <div>.
    &__textarea {
        position: relative;
        display: block; // 1
        overflow-y: hidden;
        padding-bottom: $counter-text-gap;
        min-height: $textarea-height;
        background-color: transparent;
        resize: none;
    }

    &__endIcon {
        display: flex;
        justify-content: center;
        align-items: flex-end;
        padding-bottom: $endIcon-bottom-visual-correction;
        color: var(--k-gray-500);
    }

    &__limit {
        position: initial;
    }

    &__outer {
        position: absolute;
        right: spacing.$k-spacing--s;
        bottom: $custom-bottom-spacing;
    }

    &__inner {
        display: flex;
        gap: spacing.$k-spacing--s;
    }

    em {
        background: var(--k-red-50);
    }
}
</style>
